import { FC, useEffect, useState } from 'react';
import { Modal, Form, Input, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import useThemeStore from '@/store/useThemeStore';
import SvgIcon from '@/components/SvgIcon';
import { PASSWORD_REGEX } from '@/utils/validatePassword';
import styles from './index.module.scss';

// 自定义密码输入框的眼睛图标渲染
const renderPasswordIcon = (visible: boolean) => <SvgIcon name={visible ? 'eye' : 'eye-Close'} size={20} />;

interface ChangePasswordModalProps {
  open: boolean;
  onCancel: () => void;
  onOk: (values: { currentPassword: string; newPassword: string }) => void;
}

const ChangePasswordModal: FC<ChangePasswordModalProps> = ({ open, onCancel, onOk }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [, forceUpdate] = useState({});
  const { isDarkMode } = useThemeStore();
  const [isMobile, setIsMobile] = useState(false);

  // 根据主题选择 logo
  const mobileLogo = isDarkMode ? '/logo/THEPATCH-D.png' : '/logo/THEPATCH-L.png';
  const mobileLogoSrcSet = isDarkMode ? '/logo/THEPATCH-D.png 1x, /logo/THEPATCH-D@2x.png 2x' : '/logo/THEPATCH-L.png 1x, /logo/THEPATCH-L@2x.png 2x';

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // 监听表单字段值变化，检查表单是否有效
  const values = Form.useWatch([], form);

  useEffect(() => {
    if (open) {
      // 弹窗打开时重置表单
      form.resetFields();
      setIsFormValid(false);
    }
  }, [open, form]);

  useEffect(() => {
    // 检查表单是否有效
    const checkFormValid = () => {
      const fieldsValue = form.getFieldsValue();
      const currentPassword = fieldsValue.currentPassword;
      const newPassword = fieldsValue.newPassword;
      const confirmPassword = fieldsValue.confirmPassword;

      // 所有字段都必须有值且符合验证规则
      const isValid = currentPassword && newPassword && confirmPassword && PASSWORD_REGEX.test(newPassword) && newPassword === confirmPassword;

      setIsFormValid(isValid);
    };

    checkFormValid();
  }, [values, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      onOk({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      form.resetFields();
      setIsFormValid(false);
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setIsFormValid(false);
    onCancel();
  };

  const handleFieldsChange = () => {
    // 字段变化时强制更新，触发验证检查
    forceUpdate({});
  };

  const closeIcon = (
    <svg className={styles.closeIcon} aria-hidden="true">
      <use xlinkHref="#icon-x-black" />
    </svg>
  );

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={!isMobile ? t('profile.changePasswordModal.title') : undefined}
      width={isMobile ? '100%' : 582}
      className={styles.changePasswordModal}
      wrapClassName={isMobile ? styles.changePasswordModalWrap : undefined}
      closeIcon={isMobile ? null : closeIcon}
      centered={!isMobile}
      styles={isMobile ? ({ content: { borderRadius: 0, padding: 0 }, body: { padding: 0 } } as never) : undefined}
      footer={
        <div className={styles.modalFooter}>
          <Button className="btn-secondary" onClick={handleCancel}>
            {t('profile.changePasswordModal.cancel')}
          </Button>
          <Button type="primary" size="large" onClick={handleOk} disabled={!isFormValid}>
            {t('profile.changePasswordModal.confirm')}
          </Button>
        </div>
      }
    >
      {/* 移动端自定义顶部区域 */}
      {isMobile && (
        <div className={styles.mobileHeader}>
          <img src={mobileLogo} srcSet={mobileLogoSrcSet} alt="The Patch" className={styles.mobileLogo} />
          <button className={styles.mobileCloseBtn} onClick={handleCancel} type="button" aria-label="Close">
            <SvgIcon name="x-black" size={24} />
          </button>
        </div>
      )}
      <div className={styles.modalContent}>
        {isMobile && <h2 className={styles.mobileTitle}>{t('profile.changePasswordModal.title')}</h2>}
        <Form form={form} layout="vertical" className={styles.form} requiredMark={false} onFieldsChange={handleFieldsChange}>
          <div className={styles.formContent}>
            {/* Current Password */}
            <Form.Item
              label={
                <span className={styles.label}>
                  {t('profile.changePasswordModal.currentPassword')}
                  <span className={styles.required}> *</span>
                </span>
              }
              name="currentPassword"
              rules={[
                {
                  required: true,
                  message: (
                    <div className={styles.errorWithIcon}>
                      <SvgIcon name="info" size={14} className={styles.errorIcon} />
                      <span>{t('profile.changePasswordModal.validation.currentRequired')}</span>
                    </div>
                  ),
                },
              ]}
              className={styles.formItem}
              validateTrigger="onChange"
            >
              <Input.Password placeholder={t('profile.changePasswordModal.enterPassword')} className={styles.input} iconRender={renderPasswordIcon} />
            </Form.Item>

            {/* New Password */}
            <Form.Item
              label={
                <span className={styles.label}>
                  {t('profile.changePasswordModal.newPassword')}
                  <span className={styles.required}> *</span>
                </span>
              }
              name="newPassword"
              rules={[
                {
                  required: true,
                  message: (
                    <div className={styles.errorWithIcon}>
                      <SvgIcon name="info" size={14} className={styles.errorIcon} />
                      <span>{t('profile.changePasswordModal.validation.newRequired')}</span>
                    </div>
                  ),
                },
                {
                  pattern: PASSWORD_REGEX,
                  message: (
                    <div className={styles.errorWithIcon}>
                      <SvgIcon name="info" size={14} className={styles.errorIcon} />
                      <span>{t('profile.changePasswordModal.validation.passwordWeak')}</span>
                    </div>
                  ),
                },
              ]}
              className={styles.formItem}
              validateTrigger="onChange"
            >
              <Input.Password placeholder={t('profile.changePasswordModal.enterPassword')} className={styles.input} iconRender={renderPasswordIcon} />
            </Form.Item>

            {/* Confirm New Password */}
            <Form.Item
              label={
                <span className={styles.label}>
                  {t('profile.changePasswordModal.confirmPassword')}
                  <span className={styles.required}> *</span>
                </span>
              }
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                {
                  required: true,
                  message: (
                    <div className={styles.errorWithIcon}>
                      <SvgIcon name="info" size={14} className={styles.errorIcon} />
                      <span>{t('profile.changePasswordModal.validation.confirmRequired')}</span>
                    </div>
                  ),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      <div className={styles.errorWithIcon}>
                        <SvgIcon name="info" size={14} className={styles.errorIcon} />
                        <span>{t('profile.changePasswordModal.validation.passwordMismatch')}</span>
                      </div>
                    );
                  },
                }),
              ]}
              className={styles.formItem}
              validateTrigger="onChange"
            >
              <Input.Password placeholder={t('profile.changePasswordModal.enterPassword')} className={styles.input} iconRender={renderPasswordIcon} />
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
